import React, { useState } from 'react'
import { Modal, Button, Form, Spinner, Alert, Row, Col } from 'react-bootstrap'
import { ReportFilters, SpeOption } from '../../../services/reports'

interface ReportDownloadModalProps {
  show: boolean
  onHide: () => void
  onDownload: (filters: ReportFilters, emailOverride?: string) => void
  loading?: boolean
  hasActiveJob?: boolean
  success?: boolean
  spes?: SpeOption[]
  loadingSpes?: boolean
  userEmail?: string
}

export const ReportDownloadModal: React.FC<ReportDownloadModalProps> = ({
  show,
  onHide,
  onDownload,
  loading = false,
  hasActiveJob = false,
  success = false,
  spes = [],
  loadingSpes = false,
  userEmail = ''
}) => {
  const [sourceFilter, setSourceFilter] = useState<'uau' | 'manual' | 'all'>(
    'all'
  )
  const [selectedSpeId, setSelectedSpeId] = useState<string>('')
  const [email, setEmail] = useState<string>(userEmail)
  const [dataInicio, setDataInicio] = useState<string>('')
  const [dataFim, setDataFim] = useState<string>('')
  const [forceUauRefresh, setForceUauRefresh] = useState<boolean>(false)

  React.useEffect(() => {
    if (userEmail) setEmail(userEmail)
  }, [userEmail])

  const handleDownload = () => {
    onDownload(
      {
        sourceFilter,
        speId: selectedSpeId || undefined,
        dataInicio: dataInicio || undefined,
        dataFim: dataFim || undefined,
        forceUauRefresh: forceUauRefresh || undefined
      },
      email !== userEmail ? email : undefined
    )
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Relatorio de Recebiveis</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {success ? (
          <Alert variant="success">
            <Alert.Heading>Relatorio solicitado!</Alert.Heading>
            <p>
              Seu relatorio esta sendo gerado e sera enviado para o seu email
              quando estiver pronto. Isso pode levar alguns minutos.
            </p>
          </Alert>
        ) : hasActiveJob ? (
          <Alert variant="info">
            <Alert.Heading>Relatorio em andamento</Alert.Heading>
            <p>
              Um relatorio ja esta sendo gerado. Voce recebera um email quando
              estiver pronto.
            </p>
          </Alert>
        ) : (
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                <strong>SPE / Empreendimento</strong>
              </Form.Label>
              {loadingSpes ? (
                <div className="text-center py-2">
                  <Spinner animation="border" size="sm" /> Carregando...
                </div>
              ) : (
                <Form.Control
                  as="select"
                  value={selectedSpeId}
                  onChange={(e) => setSelectedSpeId(e.target.value)}
                >
                  <option value="">Todos os empreendimentos</option>
                  {spes.map((spe) => (
                    <option key={spe.id} value={spe.id}>
                      {spe.business_name}
                    </option>
                  ))}
                </Form.Control>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Periodo</strong>
              </Form.Label>
              <Row>
                <Col>
                  <Form.Control
                    type="date"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                    placeholder="Data inicio"
                  />
                  <Form.Text className="text-muted">Inicio</Form.Text>
                </Col>
                <Col>
                  <Form.Control
                    type="date"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                    placeholder="Data fim"
                  />
                  <Form.Text className="text-muted">Fim</Form.Text>
                </Col>
              </Row>
              <Form.Text className="text-muted">
                Deixe vazio para dados acumulados (todos os periodos).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Origem dos Contratos</strong>
              </Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  id="source-all"
                  name="sourceFilter"
                  label="Todos os contratos"
                  checked={sourceFilter === 'all'}
                  onChange={() => setSourceFilter('all')}
                />
                <Form.Check
                  type="radio"
                  id="source-uau"
                  name="sourceFilter"
                  label="Somente importados do UAU"
                  checked={sourceFilter === 'uau'}
                  onChange={() => setSourceFilter('uau')}
                />
                <Form.Check
                  type="radio"
                  id="source-manual"
                  name="sourceFilter"
                  label="Somente manuais"
                  checked={sourceFilter === 'manual'}
                  onChange={() => setSourceFilter('manual')}
                />
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="force-uau-refresh"
                label="Forcar atualizacao com UAU (ignora cache do dia)"
                checked={forceUauRefresh}
                onChange={(e) => setForceUauRefresh(e.target.checked)}
              />
              <Form.Text className="text-muted">
                Por padrao, valores corrigidos sao reutilizados durante o mesmo
                dia. Marque esta opcao se suspeitar que os dados estao
                desatualizados. A geracao pode demorar bastante, dependendo da
                estabilidade do sistema de origem (UAU).
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <strong>Enviar para</strong>
              </Form.Label>
              <Form.Control
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email para receber o relatorio"
              />
              <Form.Text className="text-muted">
                O relatorio sera enviado para este email.
              </Form.Text>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        {success || hasActiveJob ? (
          <Button variant="primary" onClick={onHide}>
            Entendido
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              Cancelar
            </Button>
            <Button
              variant="success"
              onClick={handleDownload}
              disabled={loading || loadingSpes}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    style={{ marginRight: '5px' }}
                  />
                  Solicitando...
                </>
              ) : (
                <>
                  <i
                    className="feather icon-mail"
                    style={{ marginRight: '5px' }}
                  />
                  Gerar e Enviar por Email
                </>
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}
